import { isMobile } from '@/biz/tool';
import React, { useState, useEffect } from 'react';
import { normalizePriceUsd } from '@/utils/normalizePriceUsd';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { InfoCircleIcon } from '@/components/common/icon';
import { OrderStatus } from '@/interface/order';
import { AdsCampaignMarketingStrategy } from '@/interface/campaign';
import { ProductType, ProductPhysicalSelectEnum } from '../../config';
import { useTranslation } from 'react-i18next';

interface Props {
  isModal?: boolean;
  order: any;
  product: any;
}

const PriceDetails: React.FC<Props> = ({ isModal, order, product }) => {
  const { t } = useTranslation();
  const { selectedPriceFields } = useMultiplePricesStore();
  const select =
    order?.status === OrderStatus.Invited
      ? selectedPriceFields?.[0] || {}
      : order?.interestedPriceFields?.[0] || {};

  return (
    <div className="border-t border-[#EEEEEE] w-full py-[20px] mt-8">
      <div
        style={{
          display:
            order?.marketingStrategy ===
            AdsCampaignMarketingStrategy.INFLUENCER_MARKETING
              ? 'flex'
              : 'none'
        }}
        className={`self-stretch inline-flex flex-col justify-start items-start gap-4 w-full`}
      >
        <div className="self-stretch inline-flex justify-start items-center gap-4">
          <div className="justify-start text-[#1c2024] text-[19px] font-semibold font-['Plus_Jakarta_Sans'] leading-[30px]">
            {t('price_details.title', { defaultValue: 'Price Details' })}
          </div>
        </div>
        {!(
          select?.actualGet === 0 &&
          product?.productType === ProductType.PHYSICAL_PRODUCT &&
          product?.physicalProductShipType === ProductPhysicalSelectEnum.ship
        ) && (
          <>
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-[#60646c] text-sm font-medium font-['Inter'] leading-tight">
                {t('price_details.order_price', {
                  defaultValue: 'Order price'
                })}
              </div>
              <div className="text-center justify-start text-[#1c2024] text-sm font-medium font-['Inter'] leading-tight">
                {normalizePriceUsd(select?.price)}
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-end gap-2.5">
              <div
                className={`h-5 inline-flex justify-between items-center w-full`}
              >
                <div className="justify-start text-[#60646c] text-sm font-medium font-['Inter'] leading-tight">
                  {t('price_details.talent_manager_costs', {
                    defaultValue: 'AI talent manager costs'
                  })}
                </div>
                <div className="text-center justify-start">
                  {isModal && !isMobile() && (
                    <div className="px-3 mr-2 py-px bg-[#fbf057] rounded-[999px] inline-flex justify-center items-center gap-2.5">
                      <div className="justify-start">
                        <span className="text-[#1c2024] text-xs font-normal font-['Plus_Jakarta_Sans'] leading-tight">
                          {t('price_details.subsidy_supported_by', {
                            defaultValue: '🎉 Supported by '
                          })}
                        </span>
                        <span className="text-[#1c2024] text-xs font-bold font-['Plus_Jakarta_Sans'] leading-tight">
                          {t('price_details.subsidy_name', {
                            defaultValue: 'Head Million-Dollar Subsidy'
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                  <span className="text-[#1c2024] text-sm font-medium font-['Inter'] leading-tight">
                    {normalizePriceUsd(select?.talentManagerCost)}
                  </span>
                  <span className="text-[#1c2024] text-xs font-medium font-['Inter'] leading-tight">
                    {' '}
                  </span>
                  <span className="text-[#8b8d98] text-xs font-medium font-['Inter'] line-through leading-tight">
                    {normalizePriceUsd(select?.subsidyAmount)}
                  </span>
                </div>
              </div>
              {(!isModal || isMobile()) && (
                <div className="px-3 py-px bg-[#fbf057] rounded-[999px] inline-flex justify-center items-center gap-2.5">
                  <div className="justify-start">
                    <span className="text-[#1c2024] text-xs font-normal font-['Plus_Jakarta_Sans'] leading-tight">
                      {t('price_details.subsidy_supported_by', {
                        defaultValue: '🎉 Supported by '
                      })}
                    </span>
                    <span className="text-[#1c2024] text-xs font-bold font-['Plus_Jakarta_Sans'] leading-tight">
                      {t('price_details.subsidy_name', {
                        defaultValue: 'Head Million-Dollar Subsidy'
                      })}
                    </span>
                  </div>
                </div>
              )}
              {isModal && (
                <div className="self-stretch inline-flex justify-start items-start gap-1 mt-4">
                  <InfoCircleIcon />
                  <div className="flex-1 justify-start text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
                    {t('price_details.support_description', {
                      defaultValue:
                        'This helps us operate our platform and offer 24/7 customer support for your collaborations.'
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="justify-start text-[#60646c] text-sm font-medium font-['Inter'] leading-tight">
            {t('price_details.your_earnings', {
              defaultValue: 'Your earnings'
            })}
          </div>

          <div className="text-center justify-start text-[#1c2024] text-sm font-medium font-['Inter'] leading-tight">
            {!(
              select?.actualGet === 0 &&
              product?.productType === ProductType.PHYSICAL_PRODUCT &&
              product?.physicalProductShipType ===
                ProductPhysicalSelectEnum.ship
            ) && <span>{normalizePriceUsd(select?.actualGet)}</span>}
            {product?.productType === ProductType.PHYSICAL_PRODUCT &&
              product?.physicalProductShipType ===
                ProductPhysicalSelectEnum.ship && (
                <span className="text-center justify-start text-[#1c2024] text-sm font-medium font-['Inter'] leading-tight">
                  {' '}
                  {select?.actualGet !== 0 && <span>+ </span>}
                  {t('price_details.free_product', {
                    defaultValue: 'Free product'
                  })}{' '}
                  <span className="text-[#525252] text-[12px]">
                    ({normalizePriceUsd(product?.physicalProductCashValue)})
                  </span>
                </span>
              )}
          </div>
        </div>
      </div>
      {/*  */}
      <div
        style={{
          display:
            order?.marketingStrategy !==
            AdsCampaignMarketingStrategy.INFLUENCER_MARKETING
              ? 'flex'
              : 'none'
        }}
        className={`w-full flex justify-between items-center gap-4 w-full`}
      >
        <div className="justify-start text-[#1c2024] text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
          {t('price_details.deal_price', { defaultValue: 'Deal price' })}
        </div>
        <div className="flex justify-end items-center gap-2">
          <div className="justify-start text-[#1c2024] text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
            {order?.commissionRate * 100 || '--'}%{' '}
            {t('price_details.commission_text', {
              defaultValue: 'commision generated from the sales'
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceDetails;
